import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { LearnerPreferencesProvider, useLearnerPreferences } from '@/providers/LearnerPreferences';
import { clientEnv, reportMissingClientEnv } from '@/lib/env';
import { LEARNER_DESKTOP_NAV, LEARNER_MOBILE_NAV, learnerNavActive } from '@/lib/learner-nav';
import { LearnerTabIcon } from '@/components/LearnerTabIcon';
import { ModeSwitchTrigger } from '@/components/mode/ModeSwitch';
import { ModeWordmark } from '@/components/mode/ModeWordmark';
import { useSupportUnread } from '@/hooks/useSupportUnread';
import { initAnalytics, track } from '@/lib/analytics';
import { registerWebInstallation, syncLearnerAccount } from '@/lib/account-sync';
import { flushProgressQueue } from '@/lib/progress-queue';
import { LearnerAuthProvider, type LearnerAuth } from '@/providers/LearnerAuth';

export const Route = createFileRoute('/app')({ component: AppLayout });

function AppLayout() {
  // Report missing Clerk / Amplitude / Sentry, but do not brick the hub.
  // Anonymous lessons must work even when GitHub Vite secrets were unset
  // at build time. Sign-in degrades on /auth/* without a Clerk key.
  reportMissingClientEnv('learner');
  if (!clientEnv.clerkPublishableKey) return <LearnerAppShell />;
  return <ClerkLearnerAppShell />;
}

function ClerkLearnerAppShell() {
  const { getToken, isLoaded, isSignedIn, signOut } = useAuth();
  return (
    <LearnerAppShell
      auth={{
        getToken,
        isLoaded,
        isSignedIn: Boolean(isSignedIn),
        signOut: () => signOut(),
      }}
    />
  );
}

export function LearnerAppShell({ auth = null }: { auth?: LearnerAuth | null }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    initAnalytics();
    track('Web App Screen', { route: pathname });
  }, [pathname]);
  useEffect(() => {
    if (auth?.isSignedIn) {
      void syncLearnerAccount(auth.getToken).catch(() => undefined);
    } else {
      void registerWebInstallation(null).catch(() => undefined);
    }
  }, [auth?.isSignedIn]);
  useEffect(() => {
    const flush = () => void flushProgressQueue(auth?.getToken ?? null).catch(() => undefined);
    window.addEventListener('online', flush);
    flush();
    return () => window.removeEventListener('online', flush);
  }, [auth?.getToken]);

  return (
    <LearnerAuthProvider auth={auth}>
      <LearnerPreferencesProvider>
      <div className="min-h-screen bg-sky-light font-nunito text-hmong-ink lg:flex">
        <aside className="hidden w-72 shrink-0 border-r border-[#cfe8c6] bg-white p-5 lg:fixed lg:inset-y-0 lg:flex lg:flex-col">
          <DesktopModeBrand />
          <nav className="space-y-1" aria-label="Learning navigation">
            {LEARNER_DESKTOP_NAV.map((item) => {
              const active = learnerNavActive(pathname, item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => {
                    if (item.to === '/app/games') track('Games Tab Press');
                  }}
                  className={`flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-[15px] font-semibold ${
                    active ? 'bg-[#ECFFE6] text-[#2d7a13]' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {/* Desktop-only scale: keep mobile bottom tabs at 40px (#151). */}
                  <span className="grid h-8 w-8 shrink-0 place-items-center">
                    <Icon size={22} strokeWidth={2} />
                  </span>
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="mt-auto">
            {auth?.isSignedIn ? (
              <button className="w-full rounded-xl px-4 py-3 text-left text-sm font-semibold text-slate-500 hover:bg-slate-50" onClick={() => void auth.signOut()}>
                Sign out
              </button>
            ) : (
              <Link to="/auth/sign-in" className="block rounded-xl bg-[#399918] px-4 py-3 text-center text-sm font-bold text-white">
                Sign in to sync
              </Link>
            )}
          </div>
        </aside>
        <main className="min-w-0 flex-1 pb-28 lg:ml-72 lg:pb-0">
          <Outlet />
        </main>
        <nav className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-4 border-t border-[#BADBB7] bg-[#ECFEE6] px-1 pb-[max(.6rem,env(safe-area-inset-bottom))] pt-2 lg:hidden" aria-label="Learning navigation">
          {LEARNER_MOBILE_NAV.map((item) => {
            const active = learnerNavActive(pathname, item.to);
            return (
              <Link key={item.to} to={item.to} className={`relative flex flex-col items-center gap-0.5 rounded-xl py-1.5 text-[11px] font-bold ${active ? 'text-[#399918]' : 'text-[#9DA199]'}`}>
                <span className="relative">
                  <LearnerTabIcon name={item.icon} active={active} />
                  {item.to === '/app/settings' ? <SettingsUnreadDot getToken={auth?.getToken ?? null} /> : null}
                </span>
                <span className="sr-only">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
      </LearnerPreferencesProvider>
    </LearnerAuthProvider>
  );
}

function DesktopModeBrand() {
  const { dialect } = useLearnerPreferences();
  const brandRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={brandRef} className="mb-8 flex items-center gap-3 leading-none">
      <ModeSwitchTrigger source="desktop_nav" anchorRef={brandRef} />
      <ModeWordmark dialect={dialect} />
    </div>
  );
}

function SettingsUnreadDot({ getToken }: { getToken: LearnerAuth['getToken'] | null }) {
  const unread = useSupportUnread(getToken);
  if (!unread.data) return null;
  return (
    <span
      data-testid="settings-tab-unread"
      className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full border-[1.5px] border-[#ECFEE6] bg-[#E53935]"
    />
  );
}
